import { escapeHTML } from '../../utils/escape.js';

/**
 * Panel de bloqueos de disponibilidad, integrado DENTRO de la sección Reservas
 * del panel de staff (ya no es una sección/tab propia). Función de render pura:
 * devuelve un string HTML, sin estado ni efectos.
 *
 * Incluye:
 * - Un formulario para crear un nuevo bloqueo (fecha, momento, área, motivo).
 * - La lista de bloqueos existentes, cada uno con botón de eliminar.
 *
 * Estilos alineados al sistema visual público (tokens theme-aware: bg-base /
 * bg-surface / border-border-subtle / text-*; radios rounded-2xl; sin hex
 * hardcodeado). El grid del formulario se ensancha en desktop (hasta 4 columnas)
 * para aprovechar el panel full-width.
 *
 * Canonical momentos: Desayuno, Almuerzo, Merienda, Cena, Trasnoche.
 * Canonical areas: Vereda, Salón.
 */

const MOMENTOS = ['Desayuno', 'Almuerzo', 'Merienda', 'Cena', 'Trasnoche'];
const AREAS = ['Vereda', 'Salón'];

/** Clases compartidas por los inputs/selects del formulario (tokens theme-aware). */
const FIELD =
    'w-full bg-base border border-border-subtle rounded-2xl px-4 py-3 text-sm text-text-primary focus:outline-none focus:border-brand';
const FIELD_LABEL = 'block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-2';

/**
 * Contenedor del grupo de checkboxes de momento — imita el look del FIELD.
 * Grid de 2 columnas (en vez de flex-wrap) para que los momentos queden
 * alineados en una grilla prolija en lugar de envolver de forma despareja.
 */
const CHECKBOX_GROUP =
    'grid grid-cols-2 gap-x-4 gap-y-2.5 bg-base border border-border-subtle rounded-2xl px-4 py-3';
/** Etiqueta de cada checkbox (label + input inline). */
const CHECKBOX_LABEL =
    'flex items-center gap-2 text-sm text-text-primary cursor-pointer select-none';
/** El input checkbox en sí (acento brand). */
const CHECKBOX = 'w-4 h-4 rounded border-border-subtle text-brand focus:ring-brand';

/**
 * @param {{ bloque: { fecha: string, momento: string|null, area: string|null, reason: string } }} params
 * @returns {string} HTML de la fila del bloqueo
 */
function renderRow({ id, fecha, momento, area, reason }) {
    const safeId = escapeHTML(id);
    const safeFecha = escapeHTML(fecha);
    const safeReason = escapeHTML(reason);
    const momentoLabel = momento ? escapeHTML(momento) : 'todo el día';
    const areaLabel = area ? escapeHTML(area) : 'todas las áreas';

    return `
        <div class="flex items-center justify-between gap-4 py-4 border-b border-border-subtle last:border-b-0">
            <div class="flex-1 min-w-0">
                <p class="font-bold text-text-primary truncate">${safeFecha}</p>
                <p class="text-xs text-text-muted mt-1 truncate">
                    ${momentoLabel} · ${areaLabel} · ${safeReason}
                </p>
            </div>
            <button data-action="bloqueo-eliminar" data-id="${safeId}"
                class="shrink-0 px-4 py-2 rounded-xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors">
                Eliminar
            </button>
        </div>
    `;
}

/**
 * @param {{ blocks: Array<{ id: string, fecha: string, momento: string|null, area: string|null, reason: string }> }} props
 * @returns {string} HTML de la sección completa de bloqueos
 */
export function BlocksSection({ blocks = [] }) {
    const momentoCheckboxes = MOMENTOS.map(
        (m) => `
                        <label class="${CHECKBOX_LABEL}">
                            <input type="checkbox" name="momento" value="${escapeHTML(m)}" class="${CHECKBOX}" />
                            <span>${escapeHTML(m)}</span>
                        </label>`,
    ).join('');
    const areaOptions = AREAS.map(
        (a) => `<option value="${escapeHTML(a)}">${escapeHTML(a)}</option>`,
    ).join('');

    const listHtml =
        blocks.length > 0
            ? blocks.map(renderRow).join('')
            : '<p class="text-xs text-text-muted py-4">No hay bloqueos activos.</p>';

    return `
        <section aria-label="Bloqueos de disponibilidad">
            <span class="block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-2">Disponibilidad</span>
            <h3 class="text-lg font-black uppercase tracking-tight text-text-primary mb-5">Bloqueos de disponibilidad</h3>

            <form class="bg-surface border border-border-subtle rounded-2xl p-6 mb-6">
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                    <div>
                        <label class="${FIELD_LABEL}">Fecha</label>
                        <input type="date" name="fecha" required class="${FIELD}" />
                    </div>
                    <div>
                        <label class="${FIELD_LABEL}">Momento</label>
                        <div class="${CHECKBOX_GROUP}">
                            <label class="${CHECKBOX_LABEL} col-span-2 pb-2 mb-1 border-b border-border-subtle">
                                <input type="checkbox" name="momento-todos" class="${CHECKBOX}" />
                                <span>Todos</span>
                            </label>
                            ${momentoCheckboxes}
                        </div>
                    </div>
                    <div>
                        <label class="${FIELD_LABEL}">Área</label>
                        <select name="area" class="${FIELD}">
                            <option value="">Todas</option>
                            ${areaOptions}
                        </select>
                    </div>
                    <div>
                        <label class="${FIELD_LABEL}">Motivo</label>
                        <input type="text" name="reason" required placeholder="Ej. Evento privado" class="${FIELD}" />
                    </div>
                </div>
                <button type="submit" data-action="bloqueo-crear"
                    class="w-full sm:w-auto px-8 py-3 bg-brand text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg active:scale-95 transition-transform disabled:opacity-60 disabled:pointer-events-none">
                    Crear bloqueo
                </button>
            </form>

            <div class="bg-surface border border-border-subtle rounded-2xl px-5">
                ${listHtml}
            </div>
        </section>
    `;
}
